/**
 * Shape of the value `noteGoApiFetcher` throws: the parsed FastAPI response body
 * (`{ detail }`) with the numeric HTTP status attached. Use this instead of `any`
 * when inspecting a caught API error.
 */
export type ApiLikeError = {
  detail?: unknown;
  message?: unknown;
  status?: number;
  code?: string;
  name?: string;
};

/** Narrow an unknown caught value to the API error shape without asserting `any`. */
export function asApiError(error: unknown): ApiLikeError {
  return (error ?? {}) as ApiLikeError;
}

/**
 * True when a thrown API error means "this Firebase user has no backend record yet".
 * The backend signals this as a 404 and/or a `User not found` detail — check both,
 * since the fetcher surfaces the two differently depending on the response body.
 */
export function isUserNotFoundError(error: unknown): boolean {
  const err = asApiError(error);
  const message = typeof err.message === 'string' ? err.message : '';

  // `detail` is a string for FastAPI HTTPException but an array for 422
  // validation errors. Match each shape the way the original
  // `error?.detail?.includes(...)` did: substring on a string, exact element on
  // an array (NOT a stringify+substring, which would also match an unrelated
  // nested message and wrongly route the caller into "create this user").
  const detailMatches =
    typeof err.detail === 'string'
      ? err.detail.includes('User not found')
      : Array.isArray(err.detail) && err.detail.includes('User not found');

  return detailMatches || message.includes('User not found') || err.status === 404;
}

/**
 * Extract a user-facing message from a thrown API error.
 *
 * noteGoApiFetcher throws the parsed backend body (FastAPI errors are
 * `{ detail: string }`, with the numeric HTTP status attached). Surface
 * `detail` when it's a usable string; otherwise fall back to the provided
 * generic message.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as { detail?: unknown } | null;
  if (err && typeof err.detail === 'string' && err.detail.trim()) {
    return err.detail;
  }
  return fallback;
}
